import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, FileText, Mail } from 'lucide-react';
const Privacy = () => {

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-16 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 right-0 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-red-600/30 rounded-full blur-[80px] md:blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[250px] md:w-[500px] h-[250px] md:h-[500px] bg-blue-600/20 rounded-full blur-[60px] md:blur-[100px] translate-y-1/2 -translate-x-1/2"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mt-5 inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-red-600/20 mb-4 md:mb-6">
              <Shield size={24} className="text-red-500 md:w-8 md:h-8" />
            </div>
            <h1 className="text-3xl text-gray-100 md:text-5xl font-serif font-bold mb-3 md:mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
            <p className="text-xs md:text-sm text-slate-400 mt-4">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-10 md:py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
            className="space-y-6 md:space-y-8"
          >
            {/* Introduction */}
            <motion.div variants={fadeInUp} className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
              <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900 mb-3 md:mb-4">Introduction</h2>
              <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                6th Sense Training Academy ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
            </motion.div>

            {/* Information We Collect */}
            <motion.div variants={fadeInUp} className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="text-red-600 w-5 h-5 md:w-6 md:h-6" />
                <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900">Information We Collect</h2>
              </div>
              
              <div className="space-y-4 text-sm md:text-base text-slate-600">
                <div>
                  <h3 className="font-bold text-slate-800 mb-2">Personal Information</h3>
                  <p>We may collect personal information that you voluntarily provide to us when you:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-2 md:ml-4 marker:text-red-500">
                    <li>Register for courses or services</li>
                    <li>Fill out contact forms or booking forms</li>
                    <li>Subscribe to our newsletter</li>
                    <li>Contact us via email or phone</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-bold text-slate-800 mb-2">Automatically Collected Information</h3>
                  <p>When you visit our website, we may automatically collect certain information about your device, including:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-2 md:ml-4 marker:text-red-500">
                    <li>IP address, browser type, and operating system</li>
                    <li>Pages visited and time spent</li>
                    <li>Referring website addresses</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* How We Use Your Information */}
            <motion.div variants={fadeInUp} className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="text-red-600 w-5 h-5 md:w-6 md:h-6" />
                <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900">How We Use Your Information</h2>
              </div>
              <div className="text-sm md:text-base text-slate-600">
                <p className="mb-2">We use the information we collect to:</p>
                <ul className="list-disc list-inside space-y-1 ml-2 md:ml-4 marker:text-red-500">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Process your registrations and bookings</li>
                  <li>Send updates and promotional materials (with consent)</li>
                  <li>Respond to inquiries and provide support</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>
            </motion.div>

            {/* Information Sharing */}
            <motion.div variants={fadeInUp} className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="text-red-600 w-5 h-5 md:w-6 md:h-6" />
                <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900">Information Sharing</h2>
              </div>
              <div className="text-sm md:text-base text-slate-600 space-y-3">
                <p>
                  We do not sell, trade, or rent your personal information. We may share information only with:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2 md:ml-4 marker:text-red-500">
                  <li><strong>Service Providers:</strong> Trusted third parties who assist us in operating our website.</li>
                  <li><strong>Legal Requirements:</strong> If required by law or in response to valid requests.</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger or sale of assets.</li>
                </ul>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div variants={fadeInUp} className="bg-gradient-to-br from-red-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="text-red-600 w-5 h-5 md:w-6 md:h-6" />
                <h2 className="text-xl md:text-2xl font-serif font-bold text-slate-900">Contact Us</h2>
              </div>
              <p className="text-sm md:text-base text-slate-700 mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="space-y-2 text-sm md:text-base text-slate-700">
                <p><span className="font-bold">Email:</span> manager.6thsense@gmail.com</p>
                <p><span className="font-bold">Phone:</span> +91 63837 03636</p>
                <p><span className="font-bold">Address:</span> 2nd Floor, Vignesh Plaza, 202, Thillai Nagar Main Rd, above Domino's Pizza, Tennur, Tiruchirappalli, Tamil Nadu 620018, India</p>
              </div>
            </motion.div>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;